import { FilterContainer } from "@/components/layout/FilterContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { OrderSourceType } from "@/types/order-history/order-history-types";
import {
  Computer,
  Download,
  RotateCcw,
  Search,
  ShoppingBag,
  X,
} from "lucide-react";

export function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-medium text-foreground">{label}</div>
      {children}
    </div>
  );
}

export function FilterSelect<T extends string>({
  value,
  onChange,
  options,
  placeholder,
  allLabel = "All",
}: {
  value?: T;
  onChange: (value: T | undefined) => void;
  options: { label: string; value: T }[];
  placeholder: string;
  allLabel?: string;
}) {
  return (
    <Select
      value={value ?? "all"}
      onValueChange={(val) => onChange(val === "all" ? undefined : (val as T))}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function UnifiedFilters({
  activeTab,
  onTabChange,
  children,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  onReset,
  onExport,
  isExporting,
  exportDisabledReason,
}: {
  activeTab: OrderSourceType;
  onTabChange: (tab: OrderSourceType) => void;
  children: React.ReactNode;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  searchPlaceholder?: string;
  onReset?: () => void;
  onExport?: () => void;
  isExporting?: boolean;
  exportDisabledReason?: string;
}) {
  return (
    <FilterContainer
      title="Filters"
      description="Filter and search your orders"
      action={
        <div className="flex items-center gap-2">
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              disabled={isExporting || Boolean(exportDisabledReason)}
              title={exportDisabledReason}
              className="h-9"
            >
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">
                {isExporting ? "Preparing…" : "Download CSV"}
              </span>
              <span className="sm:hidden">CSV</span>
            </Button>
          )}
          {onReset && (
            <Button
              variant="outline"
              size="sm"
              onClick={onReset}
              className="h-9"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Reset Filters</span>
              <span className="sm:hidden">Reset</span>
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4 w-full">
        {onSearchChange && (
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={searchPlaceholder || "Search orders..."}
              value={searchQuery || ""}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 pr-10 h-11 w-full text-base font-medium"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8"
                onClick={() => onSearchChange("")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
        <Tabs
          value={activeTab}
          onValueChange={(v) => onTabChange(v as OrderSourceType)}
        >
          <TabsList className="grid w-full grid-cols-2 h-auto p-1">
            <TabsTrigger
              value="online"
              className="gap-2 h-10 data-[state=active]:bg-background"
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="hidden sm:inline">Online Orders</span>
              <span className="sm:hidden">Online</span>
            </TabsTrigger>
            <TabsTrigger
              value="pos"
              className="gap-2 h-10 data-[state=active]:bg-background"
            >
              <Computer className="h-4 w-4" />
              <span className="hidden sm:inline">POS Orders</span>
              <span className="sm:hidden">POS</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 w-full">
          {children}
        </div>
      </div>
    </FilterContainer>
  );
}
